import { useQuery } from "@tanstack/react-query";
import { axiosInstance } from "../../api/client";
import type { DeploymentTarget, Project, SeoPostMetaItem, SeoRedirectItem, SeoSettings } from "../../types";

interface SeoPageData {
  projects: Project[];
  settings: SeoSettings | null;
  settingsTargets: DeploymentTarget[];
  postMeta: SeoPostMetaItem[];
  postMetaTargets: Record<string, DeploymentTarget[]>;
  redirects: SeoRedirectItem[];
  redirectTargets: Record<string, DeploymentTarget[]>;
}

const EMPTY: SeoPageData = {
  projects: [],
  settings: null,
  settingsTargets: [],
  postMeta: [],
  postMetaTargets: {},
  redirects: [],
  redirectTargets: {},
};

async function fetchSeoPageData(projectId: string | null): Promise<SeoPageData> {
  const { data: projects } = await axiosInstance.get<Project[]>("/projects");

  if (!projectId) return { ...EMPTY, projects };

  const [{ data: settings }, { data: postMeta }, { data: redirects }] = await Promise.all([
    axiosInstance.get<SeoSettings | null>(`/projects/${projectId}/seo/settings`),
    axiosInstance.get<SeoPostMetaItem[]>(`/projects/${projectId}/seo/post-meta`),
    axiosInstance.get<SeoRedirectItem[]>(`/projects/${projectId}/seo/redirects`),
  ]);

  const settingsTargets = settings
    ? (await axiosInstance.get<DeploymentTarget[]>(`/projects/${projectId}/seo/settings/deployments`)).data
    : [];

  // No single endpoint lists targets for every item at once, same tradeoff `use-snippets-data.ts`
  // already accepts: fine for the item counts a project's SEO data deals with.
  const postMetaTargetEntries = await Promise.all(
    postMeta.map(async (item) => {
      const { data } = await axiosInstance.get<DeploymentTarget[]>(`/projects/${projectId}/seo/post-meta/${item.id}/deployments`);
      return [item.id, data] as const;
    })
  );

  const redirectTargetEntries = await Promise.all(
    redirects.map(async (item) => {
      const { data } = await axiosInstance.get<DeploymentTarget[]>(`/projects/${projectId}/seo/redirects/${item.id}/deployments`);
      return [item.id, data] as const;
    })
  );

  return {
    projects,
    settings,
    settingsTargets,
    postMeta,
    postMetaTargets: Object.fromEntries(postMetaTargetEntries),
    redirects,
    redirectTargets: Object.fromEntries(redirectTargetEntries),
  };
}

export function useSeoData(projectId: string | null) {
  const { data, isFetching, refetch } = useQuery({
    queryKey: ["seo-page", projectId],
    queryFn: () => fetchSeoPageData(projectId),
  });

  return {
    ...(data ?? EMPTY),
    loading: isFetching,
    refetch,
  };
}
